import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { Switch } from '@shared/ui/switch';
import { Settings, Users, CalendarDays, CalendarX, Calculator } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader';

interface ChooseFieldsProps {
  storageKey?: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const ChooseFields: React.FC<ChooseFieldsProps> = ({
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const dataLoadedRef = useRef(false);
  const [fields, setFields] = useState({
    status: false,
    category: false,
    effectiveFrom: false,
    endDate: false,
    rateStructure: false
  });

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Read Step 0 data to get selected records
  const step1Data = stepHelpers?.getStepData?.(0) || { selectedRecords: [] };
  const selectedRecords = step1Data.selectedRecords || [];
  console.log("selectedRecords",selectedRecords);

  // Check if all selected records share the exact same rate type
  const isSameRateType = selectedRecords.length > 0 && selectedRecords.every((r: any) => r.rateTypeDisplay === selectedRecords[0].rateTypeDisplay);
  const commonRateType = isSameRateType ? selectedRecords[0].rateTypeDisplay : null;

  // Restore saved data on mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData?.fieldsToEdit) {
          setFields(savedStepData.fieldsToEdit);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  const handleContinue = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    const hasAnyField = Object.values(fields).some(Boolean);
    if (!hasAnyField) {
      if (setValidationError) {
        setValidationError("Please select at least one field to edit.");
      }
      return;
    }

    saveStepData({
      fieldsToEdit: fields,
      commonRateType,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Choose Fields");
    }

    if (onNext) onNext();
  };

  const toggleField = (key: keyof typeof fields) => {
    setFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const FieldCard = ({ 
    id, 
    title, 
    description, 
    icon: Icon, 
    enabled 
  }: { 
    id: keyof typeof fields; 
    title: string; 
    description: string; 
    icon: any; 
    enabled: boolean;
  }) => (
    <div 
      className={`relative p-5 rounded-xl border-2 transition-all cursor-pointer flex gap-4 items-start ${
        enabled ? 'border-blue-600 bg-blue-50/30' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      onClick={() => toggleField(id)}
    >
      <div className={`p-2.5 rounded-lg shrink-0 ${enabled ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 pt-1">
        <h4 className={`text-base font-medium mb-1 ${enabled ? 'text-blue-900' : 'text-gray-800'}`}>
          {title}
        </h4>
        <p className={`text-sm leading-snug ${enabled ? 'text-blue-700/80' : 'text-gray-500'}`}>
          {description}
        </p>
      </div>
      
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Choose Fields to Edit</h3>
          <p className="text-gray-500">
            Toggle on the fields you want to change across all <strong>{selectedRecords.length}</strong> selected tariffs. Untoggled fields are left untouched.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldCard 
            id="effectiveFrom"
            title="Effective From"
            description="Shift the activation date of every selected record."
            icon={CalendarDays}
            enabled={fields.effectiveFrom}
          />
          <FieldCard 
            id="endDate"
            title="Effective To"
            description="Set or extend the end of the billing window."
            icon={CalendarX}
            enabled={fields.endDate}
          />
        </div>
      </div>

      {isSameRateType && (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Rate Structure</h3>
            <p className="text-gray-500">
              Adjust Rates — {commonRateType}
            </p>
          </div>
          
          <FieldCard 
            id="rateStructure"
            title="Structural Rate Adjustment"
            description="Increase or decrease every rate in the structure by a percentage or a flat amount — applied to slab tiers, TOU slots, season rates, fixed/variable components, or demand charges as appropriate."
            icon={Calculator}
            enabled={fields.rateStructure}
          />
        </div>
      )}

      {!isSameRateType && selectedRecords.length > 0 && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm">
Rate adjustment is unavailable. The selected tariffs use mixed rate types (e.g. slab + TOU), so a single rate change can't be applied uniformly. Go back to Step 1 and pick tariffs of the same type to enable this option.        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={isValidating}
          className="min-w-[120px]"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
